<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="classificationForm">
      <v-row v-for="(technology, index) in patent.patentTechnologies" :key="index">
        <v-col cols="10" xl="6" lg="6" md="6" sm="10">
          <label>{{ $t('technology') }}</label>
          <p class="label-info">
            The specific area or industry where the invention can be applied.
          </p>
          <v-autocomplete
            v-model="patent.patentTechnologies[index]"
            :items="patentTechnologiesList"
            :placeholder="'Technology'"
            :item-value="'id'"
            :item-text="'name'"
            outlined
            clearable
            return-object
          ></v-autocomplete>
        </v-col>
        <v-col
          cols="1"
          class="d-flex justify-start align-center mt-5"
          v-if="patent.patentTechnologies.length > 1"
        >
          <v-btn fab small text @click="removeTechnology(index)">
            <v-icon>
              $vuetify.icons.trash
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="hasNonEmptyTechnologies" class="mt-0 pt-0">
        <v-col cols="4">
          <v-btn
            @click="addNewTechnology"
            depressed
            rounded
            medium
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              {{ $t('Add Technology') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Optional Data for Edit -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('tags') }}</label>
                    <p class="label-info">{{ $t('tags-message') }}</p>
                    <v-combobox
                      v-model="optionalPatent.tags"
                      clearable
                      :placeholder="'Select Tags'"
                      multiple
                      persistent-hint
                      small-chips
                      return-object
                      outlined
                      hint="Type to create a new tag"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              Type and press <span class="text-bold">enter</span> to create a new
                              tag
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                      <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                          v-bind="attrs"
                          :input-value="selected"
                          :close="true"
                          @click:close="removeChip(item)"
                        >
                          <span class="pr-2">
                            {{ item }}
                          </span>
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updatePatentClassificationInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'PatentClassificationInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      patent: {
        patentTechnologies: [
          {
            id: null,
            name: '',
          },
        ],
      },
      optionalPatent: {
        tags: [],
      },
      patentTechnologiesList: [],
      unsavedChanges: false,
      status: false,
      initialPatent: {},
      initialStatus: {},
      initialTags: [],
      initialPatentTechnologies: [],
    };
  },
  watch: {
    'patent.patentTechnologies': {
      deep: true,
      handler(val) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(val) !== JSON.stringify(this.initialPatentTechnologies)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalPatent.tags': {
      deep: true,
      handler(newAccessTag) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newAccessTag) !== JSON.stringify(this.initialTags)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
  },
  mounted() {
    this.axios.get('/api/v1/technologies').then(res => {
      this.patentTechnologiesList = res.data.content;
    });

    if (this.edit) {
      this.getPatentClassificationInfo();
    }
  },
  computed: {
    hasNonEmptyTechnologies() {
      return this.patent.patentTechnologies.some(
        tech => tech.id !== null || tech.name.trim() !== '',
      );
    },
  },
  methods: {
    next() {
      const isFormValid = this.$refs.classificationForm.validate();
      if (isFormValid) {
        this.$emit('tab', 3);
        this.$emit('patentClassificationInfo', { ...this.patent });
      }
    },
    back() {
      this.$emit('tab', 1);
    },
    addNewTechnology() {
      this.patent.patentTechnologies.push({ id: null, name: '' });
    },
    removeTechnology(index) {
      this.patent.patentTechnologies.splice(index, 1);
    },
    removeChip(itemText) {
      this.optionalPatent.tags.forEach(obj => {
        if (obj === itemText) {
          this.optionalPatent.tags.splice(this.optionalPatent.tags.indexOf(obj), 1);
        }
      });
      this.optionalPatent.tags = [...this.optionalPatent.tags];
    },
    getPatentClassificationInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/patent/${id}`)
        .then(response => {
          console.log('Get Patent Classification Info', response.data);

          this.status = response.data.published;
          this.patent.patentTechnologies = response.data.patentTechnologies;
          this.optionalPatent.tags = response.data.tags;

          this.initialPatentTechnologies = JSON.parse(
            JSON.stringify(response.data.patentTechnologies),
          );

          if (response.data.tags) {
            this.optionalPatent.tags = response.data.tags;

            this.initialTags = JSON.parse(JSON.stringify(this.optionalPatent.tags));
          }

          this.initialStatus = {
            published: response.data.published,
          };

          this.$emit('status', this.initialStatus.published);
          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching patent data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getPatentClassificationInfo();
    },
    updatePatentClassificationInfo() {
      const id = this.$route.params.id;
      const tags = this.optionalPatent.tags.map(tag => tag);

      const patentUpdateData = {
        published: this.published,
        id: id,
        patentTechnologies: this.patent.patentTechnologies,
        tags: tags,
      };

      this.deleteEmptyProperties(patentUpdateData);

      const isFormValid = this.$refs.classificationForm.validate();
      if (isFormValid) {
        this.axios
          .put('api/v1/patent/classification-info', patentUpdateData)
          .then(response => {
            console.log('Patent info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Patent Updated Successfully',
            });
            this.initialPatent = { ...this.patent };
            this.initialStatus = this.status;
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);

            this.getPatentClassificationInfo();
          })
          .catch(error => {
            console.error('Error updating patent info:', error);
          });
      }
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
  },
};
</script>
